import { css, cx } from '@linaria/core'
import type React from 'react'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '#components/Button'
import { IconButton } from '#components/IconButton'
import { loadingPlaceholderTextGradientClass } from '#components/LoadingPlaceholder'
import { VariantCheckbox } from '#components/VariantCheckbox'
import CheckIcon from '#icons/check-48.svg'
import CloseIcon from '#icons/close-48.svg'
import InfoCircleIcon from '#icons/info-circle-48.svg'
import InfoCircleOutlineIcon from '#icons/info-circle-outline-48.svg'
import ReloadIcon from '#icons/reload-48.svg'
import { fragments, style } from '#styles'
import { Modal } from './Modal'

const familyLightboxContentClass = css`
  width: 50vw;
  height: 80vh;
  display: flex;
  flex-direction: column;

  > header {
    display: flex;
    align-items: center;

    gap: 8px;
    padding: 24px;

    > span {
      ${fragments.textStyle.heading.l.regular}
    }

    > h2 {
      ${fragments.textStyle.heading.l.regular}
    }

    > button {
      margin-left: auto;
    }
  }

  > ._content {
    flex: 1;
    padding-bottom: 24px;
    padding-right: 24px;
    overflow: hidden;
    display: flex;
    gap: 48px;
    width: 100%;

    > .listing {
      width: 25%;

      overflow-y: auto;

      > ul {
        list-style: none;
        padding: 0;
        margin: 0;

        > li {
          cursor: pointer;
          user-select: none;
          padding: 12px 12px 12px 24px;
          ${fragments.textStyle.body.m.regular};
          display: flex;
          align-items: flex-start;
          gap: 8px;

          > .title {
            flex: 1;
            margin-top: -2px;
            color: ${style.theme.textSoft};

            > :first-child {
              color: ${style.theme.textDefault};
            }
          }

          .action-revert:not(:hover):not(:focus-within) {
            color: ${style.theme.textSoft};
          }

          .action-hidden {
            visibility: hidden;
            pointer-events: none;
          }

          &:hover:not(:disabled) {
            background-color: ${style.theme.backgroundSurfaceTertiary};
            letter-spacing: unset;
          }

          &.selected {
            color: ${style.theme.textAccent};
            ${fragments.textStyle.body.m.bold};

            > .title {
              color: ${style.theme.textAccent};

              > :first-child {
                color: ${style.theme.textAccent};
              }
            }
          }

          .icon-fill {
            color: ${style.theme.textAccent};
          }

          .icon-default {
            color: ${style.theme.textSoft};

            &:hover {
              color: ${style.theme.textAccent};
            }
          }
        }
      }

      > main {
        padding-right: 24px;
        padding-bottom: 24px;
        overflow: auto;
      }
    }

    > .description {
      flex: 1 1;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      overflow-x: hidden;

      > .content {
        flex: 1;

        h1 {
          margin-top: 24px;
          margin-bottom: 16px;
        }

        h1._title {
          /* title -> title is also shown in the list left */
          margin-top: 0;
          padding: 0;
        }

        > div > div > :nth-child(1) p {
          /* summary paragraph */
          ${fragments.textStyle.body.m.bold}
        }

        p {
          ${fragments.textStyle.body.m.regular}
          > span {
            /* ordinary paragraphs */
            padding-bottom: 0.5em;
          }

          &.emphasized {
            ${fragments.textStyle.body.m.bold}
          }

          &.list {
            ${fragments.textStyle.heading.xxs.regular}
            color: ${style.theme.textSoft};

            > ul {
              /* benefits */

              > li {
                margin-bottom: 8px;
                ${fragments.textStyle.body.m.regular}
                color: ${style.theme.textDefault};
                display: flex;

                &:before {
                  content: "";
                  margin-top: 4px;
                  margin-right: 4px;
                  width: 16px;
                  height: 16px;
                  background-color: ${style.theme.textDefault};
                  mask-image: url(${CheckIcon});
                  mask-size: 100%;
                  mask-position: center center;
                  mask-repeat: no-repeat;
                  mask-size: contain;
                }
              }
            }
          }
        }
      }
    }
    
    &:not(:has(.listing)) {
      > .description {
        padding-left: 24px;
      }
    }

    > .extra-content {
      flex: 0 1;
      min-width: 25%;
      overflow-y: auto;
      /* padding: 0 40px; */
      display: flex;
      flex-direction: column;

      > .aside-content {
        flex: 1 1;
      }
    }

    > .extra-content > .actions, > .description > .actions {
      display: flex;
      gap: 15px;
      justify-content: flex-end;
      align-items: flex-end;
    }
  }
`

export interface MultiOverlayOption<T extends string = string> {
  /** Indicating that there will be more data loaded */
  loading?: boolean
  id: T
  source: 'ecm' | 'national-code' | 'example-content'
  displayName: React.ReactNode
  primaryContent?: React.ReactNode
  asideContent?: React.ReactNode
}

interface MultiOverlaySelectorProps<Option extends MultiOverlayOption = MultiOverlayOption> {
  open: boolean
  className?: string
  title: React.ReactNode
  initialSelectionId?: Option['id']
  selectedOptionId?: Option['id']
  options: Option[]
  onChangedPreview?: (requestedPreview: Option) => unknown
  onClosing?: (selectedOption?: Option) => unknown
  onChangedSelection?: (selectedOption: Option) => unknown
  'data-testid'?: string
}

export const TypedMultiOverlaySelector = <T extends MultiOverlayOption = MultiOverlayOption>() => {
  const Instance: FC<MultiOverlaySelectorProps<T>> = ({
    open,
    className,
    title,
    initialSelectionId,
    selectedOptionId,
    options,
    onChangedPreview = () => {
      return
    },
    onClosing = () => {
      return
    },
    onChangedSelection = () => {
      return
    },
    'data-testid': dataTestId,
  }) => {
    type OptionType = MultiOverlayOption & T

    const initialSelection = options.find((x) => x.id === initialSelectionId)
    const initialPreviewOption = options.find((x) => x.id === selectedOptionId)

    const [currentOption, setcurrentOption] = useState<OptionType | undefined>(initialSelection)
    const [currentPreviewOption, setCurrentPreviewOption] = useState<OptionType | undefined>(initialPreviewOption)
    const { t } = useTranslation('shared')

    const isChanged = currentOption?.id !== initialSelection?.id

    return (
      <Modal
        contentClassName={cx(className, familyLightboxContentClass)}
        open={open}
        lifted
        onOpenChange={() => onClosing(undefined)}
        data-testid={dataTestId}
      >
        <header>
          <h2>{title}</h2>
          <IconButton
            iconSize="l"
            iconUrl={CloseIcon}
            title={t('common.button.close', 'Close')}
            onClick={() => {
              onClosing(undefined)
            }}
          />
        </header>
        <div className="_content">
          {options.length > 1 && (
            <aside className="listing">
              <ul>
                {options.map((option, index) => {
                  const isLoading = option.loading ?? false
                  const isSelected = currentOption?.id === option.id
                  const isPreviewed = currentPreviewOption?.id === option.id
                  const canRevert = currentOption?.id !== initialSelection?.id && option.id === initialSelection?.id
                  const hasMoreDetails = !!(option.primaryContent ?? option.asideContent)
                  return (
                    <li
                      className={cx(isSelected && 'selected', isLoading && 'loading')}
                      key={option.id}
                      onClick={() => {
                        onChangedSelection(option)
                        setcurrentOption(option)
                        setCurrentPreviewOption(option)
                        // on option selected
                      }}
                    >
                      <IconButton
                        iconSize="s"
                        className={cx('action-revert', !canRevert && 'action-hidden')}
                        title={t('common.button.revert', 'Revert')}
                        iconUrl={ReloadIcon}
                        onClick={() => {
                          onChangedSelection(option)
                          setcurrentOption(option)
                          setCurrentPreviewOption(option)
                        }}
                      />
                      <VariantCheckbox
                        checked={isSelected}
                        onChange={() => {
                          setcurrentOption(option)
                        }}
                        disabled={isLoading}
                      />
                      <span className="title">
                        {isLoading ? <span className={cx(loadingPlaceholderTextGradientClass, `vary-by-${index % 3}`)} /> : option.displayName}
                      </span>
                      {hasMoreDetails && (
                        <IconButton
                          iconSize="s"
                          className={isPreviewed ? 'icon-fill' : 'icon-default'}
                          title={t('configuration.details.show', 'Show details')}
                          iconUrl={isPreviewed ? InfoCircleIcon : InfoCircleOutlineIcon}
                          onClick={(e) => {
                            e.stopPropagation()
                            onChangedPreview(option)
                            setCurrentPreviewOption(option)
                          }}
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </aside>
          )}

          <main className="description">
            <div className="content">{currentPreviewOption?.primaryContent}</div>
            {!currentPreviewOption?.asideContent && (
              <div className="actions">
                {isChanged && (
                  <Button kind="secondary" onClick={() => onClosing(undefined)}>
                    {t('common.button.cancel', 'Cancel')}
                  </Button>
                )}
                <Button onClick={() => onClosing(isChanged ? currentOption : undefined)}>
                  {isChanged ? t('common.button.apply', 'Apply') : t('common.button.back', 'Back')}
                </Button>
              </div>
            )}
          </main>
          {currentPreviewOption?.asideContent && (
            <aside className="extra-content">
              <div className="aside-content">{currentPreviewOption?.asideContent}</div>
              <div className="actions">
                {isChanged && (
                  <Button kind="secondary" onClick={() => onClosing(undefined)}>
                    {t('common.button.cancel', 'Cancel')}
                  </Button>
                )}
                <Button onClick={() => onClosing(isChanged ? currentOption : undefined)}>
                  {isChanged ? t('common.button.apply', 'Apply') : t('common.button.back', 'Back')}
                </Button>
              </div>
            </aside>
          )}
        </div>
      </Modal>
    )
  }
  return Instance
}

// biome-ignore lint/style/useComponentExportOnlyModules: is a component
export const MultiOverlaySelector = TypedMultiOverlaySelector()
